import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import type { Email } from '@/types/email';
import { AddToOrderJourneyDialog } from './AddToOrderJourneyDialog';
import { fetchOrderJourneys, linkEmailToOrderJourney } from '@/api/orderJourneyLink';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { TrackingIndicator } from '@/components/TrackingIndicator';
import { useMessageTracking } from '@/hooks/useEmailTracking';
import { SecurityBadges } from './SecurityBadges';
import { SpamScoreBadge } from '@/components/SpamScoreBadge';
import { ActionButtons } from './ActionButtons';
import type { ReplyMode } from './MessageMetadata';
import { useTrainMessage } from '@/hooks/useTrainMessage';
import { EmailTaskCreationIconButton } from './EmailTaskCreation';
import { EmailEventCreationIconButton } from './EmailEventCreation';
import { EmailProductLineCreationIconButton } from './EmailProductLineCreation';

interface DetailHeaderProps {
  email: Email;
  onBack?: () => void;
  showBackButton?: boolean;
  className?: string;
  hasPgp: boolean;
  hasSmime: boolean;
  isSent?: boolean;
  onArchive: (emailId: string) => void;
  onDelete: (emailId: string) => void;
  onReply: (mode: ReplyMode) => void;
  onSummarize?: () => void;
  onRehydrate?: (emailId: string) => void;
  rehydrating?: boolean;
  onMarkRead?: (emailId: string, read: boolean) => void;
  onAddLabel?: () => void;
  onCopyAiPayload?: () => void;
  onViewOcrData?: () => void;
  onBlockSender?: () => void;
  onMoveToFolder?: () => void;
}

export function DetailHeader({
  email,
  onBack,
  showBackButton = true,
  className,
  hasPgp,
  hasSmime,
  isSent = false,
  onArchive,
  onDelete,
  onReply,
  onSummarize,
  onRehydrate,
  rehydrating,
  onMarkRead,
  onAddLabel,
  onCopyAiPayload,
  onViewOcrData,
  onBlockSender,
  onMoveToFolder,
}: DetailHeaderProps) {
  const {
    trainAsSpam,
    trainAsGood,
    isTrainingSpam,
    isTrainingGood,
    canTrainSpam,
    canTrainGood,
  } = useTrainMessage(email, { source: 'detail_view' });
  // Local to the header on purpose: the picker needs nothing but the email,
  // so no surface above has to thread another callback down.
  const [orderJourneyOpen, setOrderJourneyOpen] = useState(false);
  const trackingEnabled = isSent && !email.draft;
  const { tracking, isLoading: trackingLoading } = useMessageTracking(
    trackingEnabled ? email.id : null
  );

  // Check if we have any badges to show
  const hasBadges = trackingEnabled || hasPgp || hasSmime || (email.spamScore !== null && email.spamScore !== undefined);

  return (
    <div className={cn('border-b border-border px-3 sm:px-5 py-2 sm:py-4 bg-card flex flex-col gap-1.5 lg:gap-0 lg:flex-row lg:items-center lg:justify-between shrink-0 z-10 sticky top-0', className)}>
      {/* No overflow-hidden here: the back button's -ml-1/-ml-2 optical nudge
          puts its left edge OUTSIDE this box, and a clip on this row shaved 8px
          off the icon. The subject wrapper below owns the truncation instead. */}
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        {showBackButton && onBack && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground -ml-1 sm:-ml-2"
            title="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 min-w-0 overflow-hidden">
          <h1 className="text-base sm:text-lg font-semibold leading-tight" title={email.subject}>
            {email.subject || '(No Subject)'}
          </h1>

          {/* Badges - inline on desktop, wrap on mobile */}
          {hasBadges && (
            <div className="items-center gap-1 shrink-0 flex-wrap hidden sm:flex">
              {trackingEnabled && (
                <TrackingIndicator
                  messageId={email.id}
                  summary={tracking?.summary ?? null}
                  events={tracking?.events ?? []}
                  isLoading={trackingLoading}
                />
              )}
              <SecurityBadges hasPgp={hasPgp} hasSmime={hasSmime} spamReport={email.spamReport} headers={email.headers} />
              {email.spamScore !== null && email.spamScore !== undefined && (
                <SpamScoreBadge
                  score={email.spamScore}
                  requiredScore={email.spamRequiredScore}
                  action={email.spamAction}
                  report={email.spamReport}
                  scannedAt={email.spamScannedAt}
                  size="sm"
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Below lg (a tablet / the Duo's inner screen in landscape) the action
          row sits under the subject; side by side it squeezed the subject into
          a ~120px column. */}
      <div className="hidden md:flex flex-wrap items-center gap-1 -ml-2 lg:ml-0 lg:pl-4 shrink-0">
        <ActionButtons
          onArchive={onArchive}
          onDelete={onDelete}
          onReply={onReply}
          emailId={email.id}
          isRead={email.read}
          canTrainSpam={canTrainSpam}
          canTrainGood={canTrainGood}
          isTrainingSpam={isTrainingSpam}
          isTrainingGood={isTrainingGood}
          onTrainSpam={trainAsSpam}
          onTrainGood={trainAsGood}
          onSummarize={onSummarize}
          onRehydrate={onRehydrate}
          rehydrating={rehydrating}
          onMarkRead={onMarkRead}
          extraActions={<><EmailTaskCreationIconButton /><EmailEventCreationIconButton /><EmailProductLineCreationIconButton /></>}
          onAddLabel={onAddLabel}
          onCopyAiPayload={onCopyAiPayload}
          onViewOcrData={onViewOcrData}
          onBlockSender={onBlockSender}
          onMoveToFolder={onMoveToFolder}
          onAddToOrderJourney={isSent ? undefined : () => setOrderJourneyOpen(true)}
        />
      </div>
      {/*
        Both halves go through sundae's server to the standalone orders app
        (§3d): the browser never holds an orders token, and no account id is
        sent — the message id is globally unique and the orders app is
        account-blind by design. Linking is one transaction over there (bridge
        grant, snapshot, blob copy, journey link), so a failure leaves nothing
        half-written and the dialog just shows the error.
      */}
      <AddToOrderJourneyDialog
        open={orderJourneyOpen}
        onOpenChange={setOrderJourneyOpen}
        vendorGuess={email.from?.name || email.from?.email || null}
        loadJourneys={() => fetchOrderJourneys(email.id)}
        onPick={async (journey) => {
          await linkEmailToOrderJourney(email.id, { orderId: journey.id, kind: 'other' });
        }}
      />
    </div>
  );
}
